import "@daypicker/react/style.css";

import { DayPicker } from "@daypicker/react";
import { format, formatISO, isPast, isValid, parse, parseISO } from "date-fns";
import type { FC } from "react";
import { useState } from "react";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { ErrorMessageBanner } from "components/forms/ErrorMessageBanner";
import {
  TimeOptionsListbox,
  formatTimeOptionForDisplay,
} from "components/forms/TimeOptionsListbox";
import { TIME_FORMAT as TIME_DISPLAY_FORMAT } from "i18n/helpers";
import { t } from "i18n/i18n";
import { CloseButton } from "mds/components/CloseButton";
import { IconText } from "mds/components/IconText";
import { Popover, PopoverButton, PopoverPanel } from "mds/components/Popover";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon, ClockIcon } from "mds/icons";
import { storeApi } from "store/index";
import { GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { formatLocalTimezoneLabel } from "utils/date";

const DATE_FORMAT = "MM/dd/yyyy";
const DATE_FORMAT_PLACEHOLDER = "MM/DD/YYYY";
const TIME_VALUE_FORMAT = "HH:mm";
const TIME_FORMAT_PLACEHOLDER = "12:00 AM";
const DEFAULT_TIME = "00:00";

const FIELD_INPUT_BASE =
  "box-border h-9 w-full min-w-0 rounded-lg border border-black-tint-70 pl-2 text-base leading-9 aria-invalid:border-red";

const formatTimeForDisplay = formatTimeOptionForDisplay;

const parseTimeFromDisplay = (display: string) => {
  const parsed = parse(display.trim(), TIME_DISPLAY_FORMAT, new Date());
  return isValid(parsed) ? format(parsed, TIME_VALUE_FORMAT) : null;
};

const parseDateInput = (value: string) => {
  const formatStr = value.match(/\/\d\d$/) ? "MM/dd/yy" : DATE_FORMAT;
  const parsed = parse(value, formatStr, new Date());
  return isValid(parsed) ? parsed : null;
};

interface PageReleaseFormProps {
  pageId: string;
  date?: Date;
  className?: string;
  close?: () => void;
}

interface FormInput {
  date: string;
  time: string;
}

export const PageReleaseForm: FC<PageReleaseFormProps> = ({
  pageId,
  className,
  date: dateProp,
  close = () => {},
}) => {
  const initialDate = dateProp ? formatISO(dateProp, { representation: "date" }) : "";
  const initialTime = dateProp
    ? formatISO(dateProp, { representation: "time" }).slice(0, 5)
    : DEFAULT_TIME;

  const form = useForm<FormInput>({
    defaultValues: { date: initialDate, time: initialTime },
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const {
    control,
    formState: { errors },
    setValue,
    watch,
    handleSubmit,
    reset,
  } = form;

  const formDate = watch("date");
  const formTime = watch("time");

  const [dateDisplay, setDateDisplay] = useState(dateProp ? format(dateProp, DATE_FORMAT) : "");
  const [timeDisplay, setTimeDisplay] = useState(() => formatTimeForDisplay(initialTime));
  const [month, setMonth] = useState(dateProp || new Date());

  const validateFuture = (date: string, time: string) => {
    if (!date || !time) return true;
    if (date === initialDate && time === initialTime) return true;
    return !isPast(parseISO(`${date}T${time}`));
  };

  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    const scheduledReleaseAt = new Date(`${data.date}T${data.time}`);

    await storeApi.pages.partial_update({
      id: pageId,
      scheduled_release_at: formatISO(scheduledReleaseAt),
    });

    trackEvent("Page - Scheduled release changed", {
      ...GENERAL_EVENT_CATEGORY,
      pageId,
      pageScheduledReleaseAt: scheduledReleaseAt.toISOString(),
    });
    close();
  };

  const onCancelSchedule = async () => {
    reset({ date: "", time: DEFAULT_TIME });
    setDateDisplay("");
    setTimeDisplay(formatTimeForDisplay(DEFAULT_TIME));

    await storeApi.pages.partial_update({
      id: pageId,
      scheduled_release_at: null,
    });

    close();
  };

  return (
    <FormProvider {...form}>
      <form
        className={twMerge(
          "page-release-form flex w-full max-w-[324px] flex-col px-2 py-1 text-black-tint-20",
          className,
        )}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex items-start justify-between gap-2">
          <h3 className="m-0 font-semibold">{t("page_release.title")}</h3>
          <CloseButton onClick={close} />
        </div>

        <p className="mb-0 mt-2 text-sm text-black-tint-20">{t("page_release.description")}</p>

        <div className="mb-2 mt-4 grid grid-cols-2 gap-1.5 text-base">
          <Popover className="min-w-0">
            <div className="relative min-w-0">
              <Controller
                control={control}
                name="date"
                render={({ field }) => (
                  <input
                    aria-invalid={errors.date ? "true" : "false"}
                    className={twMerge(FIELD_INPUT_BASE, "pr-8")}
                    placeholder={DATE_FORMAT_PLACEHOLDER}
                    ref={field.ref}
                    type="text"
                    value={dateDisplay}
                    onBlur={() => {
                      if (field.value) {
                        const parsed = parseISO(field.value);
                        if (isValid(parsed)) {
                          setDateDisplay(format(parsed, DATE_FORMAT));
                        }
                      }
                      field.onBlur();
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDateDisplay(value);
                      const parsed = parseDateInput(value);
                      if (parsed) {
                        field.onChange(formatISO(parsed, { representation: "date" }));
                        setMonth(parsed);
                      } else {
                        field.onChange("");
                      }
                    }}
                  />
                )}
                rules={{
                  required: t("page_release.date_required"),
                  validate: {
                    validateDate: (value) =>
                      isValid(parseISO(value)) || t("page_release.date_invalid"),
                    futureIfChanged: (value) =>
                      validateFuture(value, formTime) || t("page_release.date_past"),
                  },
                }}
              />

              <PopoverButton
                aria-label={t("page_release.open_calendar")}
                as={SquareButton}
                className="absolute right-0.5 top-1/2 -translate-y-1/2 hover:border-transparent hover:bg-transparent active:border-transparent active:bg-transparent data-[selected]:border-transparent data-[focus]:outline-none"
                kind="ghost"
                size="s"
              >
                <ChevronDownIcon size="xs" />
              </PopoverButton>
            </div>

            <PopoverPanel anchor={{ to: "top" }} className="!z-[106]">
              {({ close: closeDayPicker }) => (
                <DayPicker
                  disabled={{ before: new Date() }}
                  mode="single"
                  month={month}
                  selected={formDate ? parseISO(formDate) : undefined}
                  onMonthChange={setMonth}
                  onSelect={(selectedDate) => {
                    if (!selectedDate) {
                      setValue("date", "", { shouldValidate: true });
                      setDateDisplay("");
                    } else {
                      setValue("date", formatISO(selectedDate, { representation: "date" }), {
                        shouldValidate: true,
                      });
                      setDateDisplay(format(selectedDate, DATE_FORMAT));
                      setMonth(selectedDate);
                    }
                    closeDayPicker();
                  }}
                />
              )}
            </PopoverPanel>
          </Popover>

          <div className="relative min-w-0">
            <Controller
              control={control}
              name="time"
              render={({ field }) => (
                <input
                  aria-invalid={errors.time ? "true" : "false"}
                  className={twMerge(FIELD_INPUT_BASE, "pr-8")}
                  placeholder={TIME_FORMAT_PLACEHOLDER}
                  ref={field.ref}
                  type="text"
                  value={timeDisplay}
                  onBlur={() => {
                    if (field.value) {
                      setTimeDisplay(formatTimeForDisplay(field.value));
                    }
                    field.onBlur();
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTimeDisplay(value);
                    field.onChange(parseTimeFromDisplay(value) ?? "");
                  }}
                />
              )}
              rules={{
                required: t("page_release.time_required"),
                validate: {
                  validateTime: (value) =>
                    isValid(parse(value, TIME_VALUE_FORMAT, new Date())) ||
                    t("page_release.time_invalid"),
                  futureIfChanged: (value) =>
                    validateFuture(formDate, value) || t("page_release.time_past"),
                },
              }}
            />

            <TimeOptionsListbox
              ariaLabel={t("page_release.open_time_list")}
              selectedTime={formTime}
              onChange={(option) => {
                setValue("time", option, { shouldValidate: true });
                setTimeDisplay(formatTimeForDisplay(option));
              }}
            />
          </div>
        </div>

        <ErrorMessageBanner className="mb-2" name="date" />
        <ErrorMessageBanner className="mb-2" name="time" />

        <IconText
          className="mt-2 gap-1.5"
          iconStart={<ClockIcon className="text-blue" size="s" />}
          text={t("common.timezone", { timezone: formatLocalTimezoneLabel() })}
        />

        <div className="mt-8 flex flex-row items-center justify-between gap-1">
          {dateProp ? (
            <SquareButton kind="caution" size="s" onClick={onCancelSchedule}>
              {t("page_release.cancel_scheduled_release")}
            </SquareButton>
          ) : (
            <div />
          )}

          <SquareButton disabled={!formDate} kind="accent" size="s" type="submit">
            {t("common.save")}
          </SquareButton>
        </div>
      </form>
    </FormProvider>
  );
};
